import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserMd, faHospital, faHeartbeat, faAmbulance, faDollarSign, faCalendarCheck, faBaby, faShieldAlt, faHandHoldingMedical, faMicroscope, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import './Home.css';

// Actual doctor images
const heroImage = '/images/imgOne.png';
const doctorImage1 = '/images/DR-AHMED-SHETIA_flat-1.jpg'; // Dr. Ahmed Shetia
const doctorImage2 = '/images/Mohammed profile.jpg'; // Dr. Mohammed
const doctorImage3 = '/images/Dr Karan Sian.jpg'; // Dr. Karan Sian
const doctorImage4 = '/images/shaimaa.jpg'; // Dr. Shaimaa Elgayar

const Home = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [animatedElements, setAnimatedElements] = useState(new Set());
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);
  const doctorsRef = useRef(null);
  const mediaRef = useRef(null);
  const appointmentRef = useRef(null);
  // Services data - matches Services.js exactly
  const services = [
    {
      id: 'skin-cancer-check',
      title: "Skin Cancer Check",
      description: "Comprehensive skin examinations and early detection services to identify and treat skin cancers effectively.",
      icon: faShieldAlt,
      image: '/images/Skin-cancer-screening-banner-scaled.webp'
    },
    {
      id: 'employee-medical-assessments',
      title: "Employee Medical Assessments",
      description: "Occupational health services including pre-employment medicals, injury assessments, and workplace health programs.",
      icon: faHospital,
      image: '/images/employee assessment.webp'
    },
    {
      id: 'kids-health',
      title: "Kid's Health",
      description: "Comprehensive general practice care for infants, children, and adolescents, focusing on their physical, mental, and emotional well-being.",
      icon: faBaby,
      image: '/images/kids health.jpg'
    },
    {
      id: 'chronic-pain-therapy',
      title: "Chronic Pain Therapy",
      description: "Specialized treatment approaches for managing chronic pain conditions and improving quality of life.",
      icon: faHandHoldingMedical,
      image: '/images/chronicpaintherapy.webp'
    },
    {
      id: 'flu-prevention-care',
      title: "Flu Prevention Care",
      description: "Annual influenza vaccinations and immunization services to protect you and your family from seasonal flu.",
      icon: faShieldAlt,
      image: '/images/fluvaccine.jpg'
    },
    {
      id: 'womens-medical-care',
      title: "Women's Medical Care",
      description: "Comprehensive women's health services including reproductive health, pregnancy care, and specialized treatments.",
      icon: faHeartbeat,
      image: '/images/Women\'s Health & Shared Obstetric Care.jpg'
    }
  ];

  // Sample doctors data
  const doctors = [
    {
      id: 'dr-ahmed-shetia',
      name: "Dr. Ahmed Shetia",
      specialty: "General Practitioner",
      image: doctorImage1
    },
    {
      id: 'dr-mohammed',
      name: "Dr. Mohammed Mohammed",
      specialty: "General Practitioner",
      image: doctorImage2
    },
    {
      id: 'dr-shaimaa-elgayar',
      name: "Dr. Shaimaa Elgayar",
      specialty: "General Practitioner",
      image: doctorImage4
    }
  ];

  useEffect(() => {
    // Handle window resize for responsive design
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elementId = entry.target.id;
          setAnimatedElements(prev => new Set([...prev, elementId]));
          
          // Add staggered animation to child elements
          const children = entry.target.querySelectorAll('.animate-on-scroll, .animate-left, .animate-right, .animate-scale');
          children.forEach((child, index) => {
            setTimeout(() => {
              child.classList.add('animate');
            }, index * 100);
          });
        }
      });
    }, observerOptions);

    // Observe sections
    const sections = [servicesRef.current, aboutRef.current, doctorsRef.current, mediaRef.current, appointmentRef.current];
    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="home-page">
      <Helmet>
        <title>Tea Gardens GP | Tea Gardens Healthcare | Bulk Billing Medical Centre NSW</title>
        <meta name="description" content="Tea Gardens GP - Leading medical centre in Tea Gardens NSW. Bulk billing, experienced doctors, comprehensive healthcare services. Book appointment today at Shop 1&4, 209 Myall Street." />
        <link rel="canonical" href="https://teagardensgp.com.au/" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 hero-content">
              <h1>Your Trusted Family GP Practice</h1>
              <p>Comprehensive general practice care for you and your family. From routine check-ups to chronic disease management, our experienced doctors are here to support your health journey with personalized, compassionate care.</p>
              <Link to="https://www.hotdoc.com.au/medical-centres/tea-gardens-NSW-2324/tea-gardens-gp/doctors" className="btn-primary">Book an Appointment</Link>
            </div>
            <div className="col-lg-6 hero-image">
              <img 
                src={heroImage} 
                alt="Tea Gardens general practitioners" 
                onError={(e) => {
                  e.target.src = 'https://picsum.photos/600/400?random=1';
                }}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" ref={servicesRef} className="section services-section">
        <div className="container">
          <div className="section-title animate-on-scroll">
            <h2>Our Medical Services</h2>
            <p>Feel the impact of our compassionate care through our doctors. Trust in their clinical excellence and personalised approach to general practice.</p>
          </div>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <div className={`service-card animate-scale animate-delay-${(index % 6) + 1}`} key={service.id}>
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                  <div className="service-icon">
                    <FontAwesomeIcon icon={service.icon} />
                  </div>
                </div>
                <div className="service-content">
                  <h2>{service.title}</h2>
                  <p>{service.description}</p>
                  <Link to={`/services/${service.id}`} className="read-more-btn">Read More</Link>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-5 animate-on-scroll">
            <Link to="/services" className="btn-primary">View All Services</Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" ref={aboutRef} className="section about-section">
        <div className="container">
          <div className="about-container">
            <div className="about-header animate-on-scroll">
              <h2>About Tea Gardens general practitioners</h2>
              <p>Providing quality general practitioners services to the Tea Gardens community and surrounding areas.</p>
            </div>
            
            <div className="about-content-wrapper">
              <div className="about-left">
                <div className="about-description animate-left">
                  <p>At Tea Gardens GP, we are committed to providing comprehensive, compassionate general practitioners services to our community. Our experienced team of general practitioners and professionals work together to ensure you receive the highest quality care.</p>
                  <p>We believe in building lasting relationships with our patients, understanding that good health is the foundation of a fulfilling life. Our modern facility is equipped with the latest medical technology to provide accurate diagnoses and effective treatments.</p>
                </div>
                
                <div className="stats-row animate-left">
                  <div className="stat-box">
                    <div className="stat-number">15+</div>
                    <div className="stat-label">Years Experience</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-number">5000+</div>
                    <div className="stat-label">Happy Patients</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-number">Bulk Billing</div>
                    <div className="stat-label">For eligible patients</div>
                  </div>
                </div>
                
                <Link to="/doctors" className="btn-primary about-btn animate-left">Meet Our Team</Link>
              </div>
              
              <div className="about-right">
                <div className="features-grid">
                  <div className="feature-box animate-right animate-delay-1">
                    <div className="feature-icon-wrapper">
                      <FontAwesomeIcon icon={faUserMd} className="feature-icon" />
                    </div>
                    <div className="feature-text">
                      <h3>Qualified Doctors</h3>
                      <p>Our team consists of experienced and qualified general practice professionals.</p>
                    </div>
                  </div>
                  
                  <div className="feature-box animate-right animate-delay-2">
                    <div className="feature-icon-wrapper">
                      <FontAwesomeIcon icon={faHeartbeat} className="feature-icon" />
                    </div>
                    <div className="feature-text">
                      <h3>Emergency Care</h3>
                      <p>We provide efficient and immediate care for urgent medical needs.</p>
                    </div>
                  </div>
                  
                  <div className="feature-box animate-right animate-delay-3">
                    <div className="feature-icon-wrapper">
                      <FontAwesomeIcon icon={faCalendarCheck} className="feature-icon" />
                    </div>
                    <div className="feature-text">
                      <h3>Online Appointment</h3>
                      <p>Easily book your appointments online at your convenience.</p>
                    </div>
                  </div>
                  
                  <div className="feature-box animate-right animate-delay-4">
                    <div className="feature-icon-wrapper">
                      <FontAwesomeIcon icon={faDollarSign} className="feature-icon" />
                    </div>
                    <div className="feature-text">
                      <h3>Bulk Billing Consultation</h3>
                      <p>No out-of-pocket costs for eligible patients with Medicare bulk billing.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="doctors" ref={doctorsRef} className="section doctors-section">
        <div className="container">
          <div className="section-title animate-on-scroll">
            <h2>Our Experienced Team</h2>
            <p>Meet our team of experienced and dedicated general practice professionals.</p>
          </div>
          
          <div className="responsive-doctors-container" style={{
            display: 'flex',
            flexDirection: windowWidth <= 768 ? 'column' : 'row',
            gap: windowWidth <= 768 ? '25px' : '30px',
            marginTop: '40px',
            width: '100%',
            maxWidth: '1200px',
            margin: '40px auto 0 auto',
            justifyContent: 'space-between',
            alignItems: 'stretch'
          }}>
            {doctors.map((doctor, index) => (
              <div className={`doctor-card animate-scale animate-delay-${index + 1}`} key={doctor.id}>
                <div className="doctor-image">
                  <img src={doctor.image} alt={doctor.name} />
                </div>
                <div className="doctor-info">
                  <h3>{doctor.name}</h3>
                  <p>{doctor.specialty}</p>
                  <Link to={`/doctors/${doctor.id}`} className="btn-outline">View Profile</Link>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-5 animate-on-scroll">
            <Link to="/doctors" className="btn-primary">View All Doctors</Link>
          </div>
        </div>
      </section>

      {/* Media Section */}
      <section id="media" ref={mediaRef} className="section media-section">
        <div className="container">
          <div className="section-title animate-on-scroll">
            <h2>Media Gallery</h2>
            <p>Take a look at our modern general practitioners facility and professional medical services.</p>
          </div>
          
          <div className="media-grid">
            <div className="media-item animate-left animate-delay-1">
              <img src="/images/mediaOne.jpg" alt="Medical Facility Interior" />
            </div>
            
            <div className="media-item animate-left animate-delay-2">
              <img src="/images/mediaTwo.jpg" alt="Medical Consultation" />
            </div>
            
            <div className="media-item animate-left animate-delay-3">
              <img src="/images/mediaThree.jpg" alt="Medical Laboratory" />
            </div>
            
            <div className="media-item animate-left animate-delay-4">
              <img src="/images/mediaFour.jpg" alt="Patient Care" />
            </div>
            
            <div className="media-item animate-left animate-delay-5">
              <img src="/images/mediaFive.jpg" alt="General Practitioners Technology" />
            </div>
            
            <div className="media-item animate-left animate-delay-6">
              <img src="/images/mediaSix.jpg" alt="Medical Services" />
            </div>
          </div>
        </div>
      </section>

      {/* Appointment Section */}
      <section id="appointment" ref={appointmentRef} className="section appointment-section">
        <div className="container">
          <div className="appointment-content">
            <div className="row align-items-center">
              <div className="col-lg-8 animate-left">
                <h2>Book Your Appointment Today</h2>
                <p>We are dedicated to providing you with the highest quality general practice services. Whether you need a routine check-up, specialized care, or guidance on managing a health condition.</p>
              </div>
              <div className="col-lg-4 text-center text-lg-end animate-right">
                <Link to="https://www.hotdoc.com.au/medical-centres/tea-gardens-NSW-2324/tea-gardens-gp/doctors" className="btn-primary">Book an Appointment</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
